export interface AlertTagValue {
  id?: string | null;
  label: string;
  used_count?: number;
}

export interface AlertSettingsFormValues {
  owner_assignment_enabled: boolean;
  require_assignment_tag: boolean;
  require_resolution_tag: boolean;
  require_dismissal_tag: boolean;
  assignment_tags: AlertTagValue[];
  resolution_tags: AlertTagValue[];
  dismissal_tags: AlertTagValue[];
}

export interface AlertSettingsResponse {
  version: number;
  owner_assignment_enabled: boolean;
  require_assignment_tag?: boolean;
  require_resolution_tag?: boolean;
  require_dismissal_tag?: boolean;
  assignment_tags?: Array<AlertTagValue & { position?: number }>;
  resolution_tags?: Array<AlertTagValue & { position?: number }>;
  dismissal_tags?: Array<AlertTagValue & { position?: number }>;
}

export type TagListName =
  'assignment_tags' | 'resolution_tags' | 'dismissal_tags';

export const ALERT_SETTINGS_FORM_DEFAULTS: AlertSettingsFormValues = {
  owner_assignment_enabled: false,
  require_assignment_tag: true,
  require_resolution_tag: false,
  require_dismissal_tag: false,
  assignment_tags: [],
  resolution_tags: [],
  dismissal_tags: [],
};

/** Every alert-settings key in the form, to keep them out of the company fields. */
export const ALERT_SETTINGS_FORM_KEYS: string[] = Object.keys(
  ALERT_SETTINGS_FORM_DEFAULTS
);

const toTagValues = (
  tags: AlertSettingsResponse['assignment_tags']
): AlertTagValue[] =>
  (tags ?? []).map((tag) => ({
    id: tag.id,
    label: tag.label,
    used_count: tag.used_count,
  }));

/** The API owns the tag lists, their ids and usage counts; nothing is made up here. */
export const alertSettingsToFormValues = (
  settings?: AlertSettingsResponse
): AlertSettingsFormValues => ({
  owner_assignment_enabled:
    settings?.owner_assignment_enabled ??
    ALERT_SETTINGS_FORM_DEFAULTS.owner_assignment_enabled,
  require_assignment_tag:
    settings?.require_assignment_tag ??
    ALERT_SETTINGS_FORM_DEFAULTS.require_assignment_tag,
  require_resolution_tag:
    settings?.require_resolution_tag ??
    ALERT_SETTINGS_FORM_DEFAULTS.require_resolution_tag,
  require_dismissal_tag:
    settings?.require_dismissal_tag ??
    ALERT_SETTINGS_FORM_DEFAULTS.require_dismissal_tag,
  assignment_tags: toTagValues(settings?.assignment_tags),
  resolution_tags: toTagValues(settings?.resolution_tags),
  dismissal_tags: toTagValues(settings?.dismissal_tags),
});

const toTagPayload = (tags: AlertTagValue[] | undefined) =>
  (tags ?? []).map((tag) => ({ id: tag.id, label: tag.label.trim() }));

export const alertSettingsToPayload = (values: AlertSettingsFormValues) => ({
  owner_assignment_enabled: values.owner_assignment_enabled,
  require_assignment_tag: values.require_assignment_tag,
  require_resolution_tag: values.require_resolution_tag,
  require_dismissal_tag: values.require_dismissal_tag,
  assignment_tags: toTagPayload(values.assignment_tags),
  resolution_tags: toTagPayload(values.resolution_tags),
  dismissal_tags: toTagPayload(values.dismissal_tags),
});
